import { _axios } from "../../utils/axios";
import { extractFieldValues } from "./Fieldmapping";
/**
 * BEFORE - without the Offline Tentative list and helper:
 *
 * import {
 *     APPLICATION_NOTE_EXCLUDED_DOCUMENT_IDS,
 *     PERMANENT_ONLY_DOCUMENT_IDS,
 *     RL_APPLICATION_TYPE,
 *     RL_APPLICATION_TYPE_OPTION_ID,
 * } from "../../config/phaseConstants";
 */
import {
    APPLICATION_NOTE_EXCLUDED_DOCUMENT_IDS,
    OFFLINE_TENTATIVE_ONLY_DOCUMENT_IDS,
    PERMANENT_ONLY_DOCUMENT_IDS,
    RL_APPLICATION_TYPE,
    RL_APPLICATION_TYPE_OPTION_ID,
    isOfflineTentativeTypeValue,
    sortByDocumentDisplayOrder,
} from "../../config/phaseConstants";

interface ApplicationData {
    applicationId?: string;
    [key: string]: any;
}

type TRegistrationDocumentConfig = {
    document_config_id: number;
    document_label: string;
    mandatory: 0 | 1;
    active: 0 | 1;
};

/**
 * Same Building Plan/Site Plan (construction-based) and K Prat/Registry
 * Certificate (Permanent-RL-only) filtering RenderField.tsx already applies
 * when rendering the live "Documents" step - reused here so the printed
 * Application Note always lists exactly the documents that step would
 * actually ask for, instead of a fixed static list.
 */
function filterRegistrationDocuments(
    configs: TRegistrationDocumentConfig[],
    { isThereConstruction, rlApplicationType }: { isThereConstruction: boolean; rlApplicationType: unknown }
) {
    const strAppType = String(rlApplicationType ?? "").trim().toLowerCase();
    const isPermanent =
        rlApplicationType === RL_APPLICATION_TYPE_OPTION_ID.PERMANENT ||
        rlApplicationType === RL_APPLICATION_TYPE.PERMANENT ||
        strAppType === "9993" ||
        strAppType === "permanent";

    /**
     * BEFORE - the checklist followed document-id order:
     *   return configs.filter((config) => { ... });
     * It is now sorted into DOCUMENT_DISPLAY_ORDER, so the printed checklist
     * matches the upload step and the reviewer screens.
     */
    const listedConfigs = configs.filter((config) => {
        if (!config.active) return false;
        /**
         * The signed Application Form is uploaded after this PDF is downloaded
         * and signed, so it cannot appear in the checklist printed inside it.
         */
        if (APPLICATION_NOTE_EXCLUDED_DOCUMENT_IDS.includes(config.document_config_id)) {
            return false;
        }
        if (config.document_config_id === 1) return isThereConstruction === true;
        if (config.document_config_id === 2) return isThereConstruction === false;
        /**
         * Same rule the Documents step applies: Offline-Tentative-only
         * documents are listed only for that type, checked before the
         * Permanent-only list because Registry Certificate is in both.
         */
        if (OFFLINE_TENTATIVE_ONLY_DOCUMENT_IDS.includes(config.document_config_id)) {
            return isOfflineTentativeTypeValue(rlApplicationType);
        }
        if ((PERMANENT_ONLY_DOCUMENT_IDS as readonly number[]).includes(config.document_config_id)) {
            return isPermanent;
        }
        return true;
    });

    return sortByDocumentDisplayOrder(
        listedConfigs,
        (config) => config.document_config_id
    );
}

/**
 * Generates the applicant's pre-submission "Download Form" PDF (application
 * form + receipt slip) via the backend's Puppeteer-based renderer
 * (templates/application-download-note-bahadura.hbs), the same pipeline
 * already used for the Demand Note and Certificate PDFs. This replaces the
 * previous client-side html2pdf.js approach, which rendered the whole page
 * as a screenshot and produced ~20MB files - the backend renders real text,
 * so the output is a normal-sized PDF.
 */
export async function generateFilledPDFDocument(
    rawData: ApplicationData,
    formDocumentConfigs: TRegistrationDocumentConfig[] = []
): Promise<Blob> {
    const applicationId = rawData.applicationId || rawData.application_id || rawData.id || "";
    const data = extractFieldValues(rawData);

    const isConstructionPresent =
        String(data.is_there_construction) === "होय" ||
        String(data.is_there_construction) === "true" ||
        String(data.is_there_construction) === "1";
    const constructionArea = data.construction_area && data.construction_area !== "NaN"
        ? data.construction_area
        : "0";

    /**
     * Same admin-configured logo the Demand Note PDF already uses
     * (autoFill?.data?.[0]?.demand_note_logo via GET
     * /api/controller/auto-fill, backed by cp_system_settings) - not the
     * separate standalone /api/logo upload, and not certificate_logo either
     * (Demand Note and Certificate are configured as two independent
     * logos in the admin's Template Config). Updating the Demand Note logo
     * there updates this document's logo too.
     */
    let logoUrl = "";
    try {
        const autoFillResponse = await _axios.get("/controller/auto-fill");
        if (autoFillResponse?.data?.success === 1) {
            logoUrl = autoFillResponse?.data?.data?.[0]?.demand_note_logo || "";
        }
    } catch (e) {
        // Logo is a visual nice-to-have on this form - proceed without it
        // rather than failing the whole download over an auto-fill fetch error.
    }

    /**
     * formDocumentConfigs comes from the live application form's already-
     * fetched steps (cp_form_documents_mappings-driven - the real "Documents"
     * step data RenderField.tsx renders from). This is NOT the same list as
     * GET /document-config/regestration, which only returns rows tagged
     * document_Type = "Registration Document" (a single, unrelated document -
     * Aadhar Card at account-registration time). Fetching that endpoint here
     * was the original bug: it made every document except Aadhar Card
     * disappear from this PDF's checklist.
     */
    const documents = filterRegistrationDocuments(formDocumentConfigs, {
        isThereConstruction: rawData.is_there_a_construction === true,
        rlApplicationType: rawData.rl_application_type,
    });

    const response = await _axios.post(
        "/applications/generate-download-note",
        {
            data: {
                ...data,
                applicationId,
                construction_area: constructionArea,
                showConstructionArea: isConstructionPresent && Number(constructionArea) > 0,
                logo: logoUrl,
                documents,
            },
        },
        { responseType: "blob" }
    );

    return response.data;
}
